import { Object3D } from 'three';

import { IObject3DStatus } from '@/types';

import { AnnoFactory } from '../annos';

export class AnnoSelects {
  /** 已选中的对象集合 */
  private selected: Set<string>;
  /** 通过 name 获取 Object3D 对象的方法 */
  private getObjectByName: (name: string) => Object3D | undefined;

  constructor(api: (name: string) => Object3D | undefined) {
    this.selected = new Set();
    this.getObjectByName = api;
  }

  /**
   * 切换标注对象选择态或非选择态
   * @param target
   * @param targetStatus
   * @returns
   */
  private toggleSelectStatus(target: Object3D, targetStatus: IObject3DStatus) {
    const currStatus = target.userData.status;

    // 在设置状态为 'default' 时，避免影响到非 'select' 状态的对象（例如 'hover' 或者 'adjust'）
    if (targetStatus === 'default' && currStatus !== 'select') {
      return;
    }

    currStatus !== targetStatus && AnnoFactory.setStatus(target, targetStatus);
  }

  /**
   * 通过 name 切换标注对象选择态或非选择态
   * @param name
   * @param status
   * @returns 是否有这个标注物
   */
  private toggleSelectStatusByName(name: string, status: IObject3DStatus) {
    const target = this.getObjectByName(name);
    if (!target) return false;
    this.toggleSelectStatus(target, status);
    return true;
  }

  public has(name: string) {
    return this.selected.has(name);
  }

  public add(target: Object3D) {
    this.selected.add(target.name);
    this.toggleSelectStatus(target, 'select');
  }

  public delete(target: Object3D, status: IObject3DStatus = 'default') {
    this.selected.delete(target.name);
    this.toggleSelectStatus(target, status);
  }

  /**
   * 设置标注物选中集合
   * @param newSelects
   * @returns
   */
  public flush(newSelects: Set<string>) {
    const toDelete: Set<string> = new Set();

    this.selected.forEach((name) => {
      if (!newSelects.delete(name)) {
        toDelete.add(name);
      }
    });

    if (newSelects.size > 0 || toDelete.size > 0) {
      toDelete.forEach((name) => {
        this.toggleSelectStatusByName(name, 'default');
        // 就算当前 Sight 不存在该标注物，也要从 selected 中删除
        this.selected.delete(name);
      });
      newSelects.forEach((name) => {
        if (this.toggleSelectStatusByName(name, 'select')) {
          this.selected.add(name);
        }
      });
    }

    return this.selected;
  }

  /**
   * 恢复当前选择的标注物状态，并清空集合数据
   */
  public reset() {
    if (this.selected.size === 0) return;

    this.selected.forEach((name) => {
      this.toggleSelectStatusByName(name, 'default');
    });
    this.selected.clear();
  }
}
